import React, {useEffect, useState} from "react";
import {Stack, Skeleton, Box, Paper, useTheme, alpha, Pagination} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useGatePass from "../hooks/useGatePass";
import CustomChipComponent from "../../../core/components/common/custom-chip.component";
import {QRCodeSVG} from "qrcode.react";
import moment from "moment";
import CustomTableLoaderComponent from "../../../core/components/common/custom-table-loader.component";
import ListGatePassVehicleAccessLogComponent from "../components/access-logs/list-gatepass-vehicle-access-logs.component";
import useUserProfile from "../../ProfilePlugin/hooks/useUserProfile";


const headers = ["Plate Number", "Entry Type", "Purpose", "Timestamp"];



const ViewGatePassPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const {isFetchingGatePassDetails, gatePassDetailsData, viewGatePass, isFetchingGatePassAccessLogs,
        getGatePassVehicleAccessLogs, gatePassVehicleAccessLogs, getOrgGatePassVehicleAccessLogs, orgGatePassVehicleAccessLogs
    } = useGatePass();

    const {viewProfile, profileDetails} = useUserProfile();

    const gatePassId = new URLSearchParams(location.search).get('id');
    const orgUID = new URLSearchParams(location.search).get("org_uid");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const org_uid = rbacStorage?.state?.currentProfile?.orgId || null;

    const rowsPerPage = 10;

    const [gatepassAccessLogsData, setGatepassAccessLogsData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);


    useEffect(() => {
        viewProfile()
    },[]);
    
    useEffect(() => {
        const parameters = {gatePassUID: gatePassId, orgUID: org_uid || orgUID};

        viewGatePass(parameters);
    }, [gatePassId, org_uid, orgUID]);   

    useEffect(() => {
            const parameters = {
                page: currentPage,
                limit: rowsPerPage,
                gatepassUID: gatePassId,
                orgUID: org_uid || orgUID
            }

            getGatePassVehicleAccessLogs(parameters);
    }, [gatePassId, , org_uid, orgUID]);

    useEffect(() => {
        if (orgGatePassVehicleAccessLogs) {
            setGatepassAccessLogsData(gatePassVehicleAccessLogs.content);
            setTotalPages(gatePassVehicleAccessLogs.totalPages);   
        }
    }, [gatePassVehicleAccessLogs, orgGatePassVehicleAccessLogs]);

    useEffect(() => {
        if (gatePassId && currentPage) {
            const parameters = {
                page: currentPage,
                limit: rowsPerPage,
                gatepassUID: gatePassId,
                orgUID: org_uid || orgUID
            };  

            getGatePassVehicleAccessLogs(parameters);
        }
    }, [gatePassId, org_uid, orgUID, currentPage]);


    const handleChangePagination = (event, page) => { 
        setCurrentPage(page); 
    }

    const handleBack = () => {
        navigate("/gatepass");
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Gate Pass List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{textAlign: "start", mt: 2}}>
                <CustomTypography 
                    variant="h3"
                    text={"Gate Pass Details"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"View and manage gate pass information"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack spacing={0} flex={1}>
                        <CustomTypography 
                            variant="caption" 
                            text={"Pass ID"} 
                            customStyles={{
                                marginBottom: 0, 
                            }}
                        />
                    
                        {(isFetchingGatePassDetails) ?
                            <Skeleton variant="text" height="35px" width="34%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                        :
                            <CustomTypography
                                variant="body1"
                                text={(gatePassDetailsData?.gate_pass?.gatepass_uid)}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontWeight: 600,
                                }}
                            />
                        }
                    </Stack>
                </Stack>

                <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                    <Stack spacing={2} flex={1}>
                        <Box>
                            <CustomTypography 
                                variant="caption" 
                                text={"Plate Number"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomTypography
                                    variant="body1"
                                    text={(gatePassDetailsData?.gate_pass?.plate_number)}
                                    customStyles={{
                                        marginBottom: 0, 
                                        fontWeight: 600,
                                    }}
                                />
                            }
                        </Box>

                        <Box sx={{display: "inline-grid"}}> 
                            <CustomTypography 
                                variant="caption" 
                                text={"Pass Type"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomChipComponent category="gatePassType"
                                    label={(gatePassDetailsData?.gate_pass?.pass_type === "Single Entry Pass") ? "Single Entry" 
                                        : 
                                            (gatePassDetailsData?.gate_pass?.pass_type === "Multi Entry Pass") ? "Multi Entry"
                                        :
                                            (gatePassDetailsData?.gate_pass?.pass_type === "Annual Pass") ? "Annual"
                                        :
                                            (gatePassDetailsData?.gate_pass?.pass_type === "Special Entry Pass") ? "Special Entry"
                                        :
                                        gatePassDetailsData?.gate_pass?.pass_type
                                    }
                                />
                            }
                        </Box>

                        <Box>
                            <CustomTypography 
                                variant="caption" 
                                text={"Issue Date"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomTypography
                                    variant="body1"
                                    text={`${moment(gatePassDetailsData?.gate_pass?.created_time).format("MM/DD/YYYY")}`}
                                    customStyles={{
                                        marginBottom: 0, 
                                        fontWeight: 600,
                                    }}
                                />
                            }
                        </Box>

                        {(gatePassDetailsData?.gate_pass?.status !== "Active" && gatePassDetailsData?.gate_pass?.status !== "For Approval") && 
                            <Box>
                                <CustomTypography 
                                    variant="caption" 
                                    text={"Reason"} 
                                    customStyles={{
                                        marginBottom: 0, 
                                    }}
                                />

                                {(isFetchingGatePassDetails) ?
                                    <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                                :
                                    <CustomTypography
                                        variant="body1"
                                        text={(gatePassDetailsData?.gate_pass?.purpose || "N / A")}
                                        customStyles={{
                                            marginBottom: 0, 
                                            fontWeight: 600,
                                        }}
                                    />
                                }
                            </Box>
                        }
                    </Stack>

                    <Stack spacing={2} flex={1}>
                        <Box>
                            <CustomTypography 
                                variant="caption" 
                                text={"Entry Type"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomTypography
                                    variant="body1"
                                    text={(gatePassDetailsData?.gate_pass?.entry_type)}
                                    customStyles={{
                                        marginBottom: 0, 
                                        fontWeight: 600,
                                    }}
                                />
                            }
                        </Box>

                        <Box sx={{display: "inline-grid"}}>
                            <CustomTypography 
                                variant="caption" 
                                text={"Status"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomChipComponent category="gatePassStatus"
                                    label={(gatePassDetailsData?.gate_pass?.status === "Active") ? "Active" 
                                        : 
                                            (gatePassDetailsData?.gate_pass?.status === "Deactivated") ? "Deactivated"
                                        :
                                            (gatePassDetailsData?.gate_pass?.status === "Suspended") ? "Suspended"
                                        :
                                            (gatePassDetailsData?.gate_pass?.status === "Denied") ? "Denied"
                                        :
                                            (gatePassDetailsData?.gate_pass?.status === "For Approval") ? "For Approval"
                                        :
                                            gatePassDetailsData?.gate_pass?.status
                                    }
                                />
                            }
                        </Box>

                        <Box>
                            <CustomTypography 
                                variant="caption" 
                                text={"Validity Period"} 
                                customStyles={{
                                    marginBottom: 0, 
                                }}
                            />
                            
                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="text" height="35px" width="70%" sx={{backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <CustomTypography
                                    variant="body1"
                                    text={`${moment(gatePassDetailsData?.gate_pass?.valid_from).format("MM/DD/YYYY HH:mm A")} - ${moment(gatePassDetailsData?.gate_pass?.valid_to).format("MM/DD/YYYY HH:mm A")}`}
                                    customStyles={{
                                        marginBottom: 0, 
                                        fontWeight: 600,
                                    }}
                                />
                            }
                        </Box>
                    </Stack>

                    <Stack spacing={2} flex={1}>
                        <Box sx={{display: "inline-grid"}}>
                            <CustomTypography variant="caption" text={"QR Code"} />

                            {(isFetchingGatePassDetails) ?
                                <Skeleton variant="rectangular" height="200px" width="200px" sx={{borderRadius: 1, backgroundColor: `${alpha(theme.palette.primary.main, 0.09)}`}} />
                            :
                                <QRCodeSVG value={gatePassDetailsData?.online_qr} style={{width: 200, height: 200}} />
                            }   
                        </Box>
                    </Stack>
                </Stack>           
            </Paper>

            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Stack direction={{xs: "column", md: "column"}} spacing={2} sx={{mb: 3}}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flex={1}>
                        <Box sx={{ textAlign: "start" }}>
                            <CustomTypography 
                                variant="h3"
                                text={"Access Logs"}
                                customStyles={{
                                    marginBottom: 0, 
                                    fontSize: 20,
                                    fontWeight: 700
                                }}
                            />

                            <CustomTypography 
                                variant="caption"
                                text={"Monitor vehicle access logs"}
                                customStyles={{
                                    marginBottom: 0,
                                    fontSize: 14 
                                }}
                            />
                        </Box>
                    </Stack>
            
                    <Box sx={{mt: "8px !important"}}>
                        {(isFetchingGatePassAccessLogs) ?
                            <Box sx={{mt: 3}}>
                                <CustomTableLoaderComponent 
                                    headers={headers} 
                                    hasAction={false}
                                /> 
                            </Box>
                        :   
                            <Box sx={{mt: 3}}>
                                <ListGatePassVehicleAccessLogComponent 
                                    headers={headers} 
                                    data={gatepassAccessLogsData} 
                                />

                                {gatepassAccessLogsData?.length > 0 &&
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handleChangePagination}
                                        sx={{
                                            margin: "auto",
                                            width: "fit-content",
                                            paddingTop: 3,

                                            "& .MuiPaginationItem-root.Mui-selected": {
                                                backgroundColor: "#0b99da",      
                                                color: "#fff",               
                                            }
                                        }}
                                    />
                                }   
                            </Box>
                        }
                    </Box>
                </Stack>
            </Paper>
       </>
    )
}

export default ViewGatePassPage